import Phaser from "phaser";
import {
  EventBus,
  ListenForReactEvent,
  ListenUpdateObjective,
  NotifyReact,
  ReactToExerciseEventType,
  RemoveListener,
  ShowExercise,
} from "../EventBus";
import RoundedRect from "../GameObjects/RoundedRect";
import { BaseNote, getMidiNumberFromNote, Note } from "Phaser/Util/OSMDHelper";
import { colors } from "Phaser/config";
import ExerciseBase, { IConfig, IObjective } from "./ExerciseBase";
import { Dissolve } from "Phaser/Rendering/Transitions";
import {
  MediumConfig,
  MinimumConfig,
  PhraseData,
  PhraseGraphic,
  StaffTransition,
} from "Phaser/GameObjects/StaffLine";
import { noteFromOsmdPitch } from "Phaser/Util/OSMDHelper";
import { ProgressState } from "Phaser/GameObjects/Progress";
import { PhraseObjectiveData } from "./StaffExercise";
import TimeSignature from "Models/TimeSignature";
import { PhraseDataUntimed } from "Phaser/GameObjects/StaffLine/PhraseGraphicUntimed";
import { optimizeStaffForOptions } from "Phaser/GameObjects/StaffLine/Util";
import NoteGraphic from "Phaser/GameObjects/StaffLine/Note";
import { VoiceData } from "Phaser/GameObjects/StaffLine/Types";
import { COLORBLIND_TYPE } from "Utils/Constants";

interface Objective extends IObjective {
  phrases?: PhraseObjectiveData[];
  // global format can be overridden per phrase
  formatConfig?: FormatConfig;
}

interface FormatConfig {
  line_gap_vertical?: number;
  padding_x?: number;
  centre_align_horizontal?: boolean;
  line_thickness?: number;
  width?: number;
  clef_gap?: number;
  clef_padding?: number;
  first_note_offset?: number;
}

export interface Config extends IConfig<Objective>, FormatConfig {
  cbType?: COLORBLIND_TYPE;
}

function DefaultConfig(config: Config): Config {
  const dpr = 1;
  const line_gap_vertical = (config.line_gap_vertical ?? 20) * dpr;
  const padding_x = (config.padding_x ?? 50) * dpr;
  const line_thickness = (config.line_thickness ?? 2) * dpr;
  const width = (config.width ?? 650) * dpr;
  const clef_gap = (config.clef_gap ?? 60) * dpr;
  const clef_padding = (config.clef_padding ?? 10) * dpr;
  const first_note_offset = (config.first_note_offset ?? 50) * dpr;
  if (config.chapterIdx === undefined)
    console.warn(
      "chapter index not provided! this will prevent objectives from loading",
    );
  const chapterIdx = config.chapterIdx;
  return {
    objectives: config.objectives ?? [],
    firstObjectiveAppearDelay: config.firstObjectiveAppearDelay ?? 0,
    line_gap_vertical,
    padding_x,
    line_thickness,
    width,
    clef_gap,
    clef_padding,
    first_note_offset,
    centre_align_horizontal: config.centre_align_horizontal,
    chapterIdx,
    cbType: config.cbType,
  };
}

function getLineNumber(note: BaseNote) {
  switch (note) {
    case "C":
      return 6;
    case "D":
      return 5;
    case "E":
      return 4;
    case "F":
      return 3;
    case "G":
      return 2;
    case "A":
      return 1;
    case "B":
      return 0;
  }
}

/*function getLineNumberOctave(note: Note, octave: number) {
  return getLineNumber(note) + octave * 7;
}*/

class NoTimeCursor extends RoundedRect {
  endX: number;
  currentNote: number = 0;
  onFinish: () => void;
  cbCtx?: object;
  db: NoTimeExercise;
  isPaused: boolean = false;

  heldKeys: Set<number> = new Set([]);
  timers: Phaser.Time.TimerEvent[] = [];
  timedOutKeys: boolean[] = [];
  wrongNotes: boolean[] = [];
  colorblindType?: COLORBLIND_TYPE;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    endX: number,
    db: NoTimeExercise,
    onFinish: () => void,
    cbCtx?: object,
    colorblindType?: COLORBLIND_TYPE,
  ) {
    const color = colors.cursor;
    const radius = width / 2;
    super(scene, x, y, width, height, radius, color, 0);
    this.colorblindType = colorblindType;
    this.currentNote = 0;
    this.onFinish = onFinish;
    this.cbCtx = cbCtx;
    this.db = db;
    this.endX = endX;
  }

  findNoteByMidi(midi: number) {
    if (
      this.currentNote < 0 ||
      this.currentNote >= this.db.getAllSlices().length
    )
      return undefined;
    else {
      const notes = this.db.getNotes(this.currentNote);
      if (notes) {
        const _id = notes.find(
          (n) =>
            n.voice !== "rest" &&
            getMidiNumberFromNote(
              `${n.voice.note[0]}${n.voice.note[1]}` as Note,
            ) === midi,
        )?.id;
        if (_id) return this.db.getNote(_id);
      }
    }
  }

  keyTimeout(midi: number) {
    if (this.timers[midi]) {
      this.timers[midi].remove();
      this.timedOutKeys[midi] = false;
    }
    this.timers[midi] = this.scene.time.delayedCall(
      250,
      () => (this.timedOutKeys[midi] = true),
      [],
      this,
    );
  }

  onPressKey(midi: number) {
    // Don't process key presses when paused to prevent auto-complete on resume
    if (this.isPaused) {
      return;
    }
    if (
      this.currentNote < 0 ||
      this.currentNote >= this.db.getAllSlices().length
    )
      return;
    else {
      this.heldKeys.add(midi);
      this.keyTimeout(midi);
      const note = this.findNoteByMidi(midi);
      if (note) {
        note.setTimingState(ProgressState.Ended, this.colorblindType);
        note.pulse(this.colorblindType);
      } else {
        const found = this.db
          .getNotes(this.currentNote)
          .map((n) => this.db.getNote(n.id))
          .filter((n) => n?.timingState === undefined)
          .sort(
            (a, b) =>
              Math.abs((a as NoteGraphic).midiNote - midi) -
              Math.abs((b as NoteGraphic).midiNote - midi),
          );
        if (found.length > 0) {
          found[0]!.shake();
          this.wrongNotes[(found[0] as NoteGraphic).midiNote] = true;
        }
      }
      if (this.checkNotes()) {
        this.currentNote++;
        let target = 0;
        if (this.currentNote === this.db.getAllSlices().length) {
          target = this.endX;
          this.onFinish.call(this.cbCtx);
        } else {
          target =
            this.db.getNotePosition(this.db.getNotes(this.currentNote)[0].id) ??
            0;
        }
        this.move(target, 4);
      }
    }
  }

  onReleaseKey(midi: number) {
    const note = this.findNoteByMidi(midi);
    if (note) {
      note.reset(true);
    }
    this.heldKeys.delete(midi);
    this.timedOutKeys[midi] = false;
    if (this.heldKeys.size === 0) {
      this.db.getNotes(this.currentNote)?.forEach((n) => {
        const note = this.db.getNote(n.id);
        note?.reset(true);
        this.wrongNotes[(note as NoteGraphic).midiNote] = false;
      });
      this.timedOutKeys.clear();
    }
  }

  checkNotes() {
    if (
      this.currentNote < 0 ||
      this.currentNote >= this.db.getAllSlices().length
    )
      return false;
    else {
      const notes = this.db
        .getNotes(this.currentNote)
        .map((n) => this.db.getNote(n.id));
      console.debug(this.db.getNotes(this.currentNote));
      console.debug(notes);
      if (notes) {
        const timeout =
          notes.find(
            (note) => this.timedOutKeys[(note as NoteGraphic).midiNote],
          ) !== undefined;
        let played = true;
        for (const note of notes) {
          if (!note) continue;
          console.debug(note, note?.timingState !== ProgressState.Ended);
          console.debug(
            "timeout",
            this.timedOutKeys[(note as NoteGraphic).midiNote],
          );
          if (timeout && !this.wrongNotes[(note as NoteGraphic).midiNote])
            note.setTimingState(ProgressState.MistimedRelease);
          if (note?.timingState !== ProgressState.Ended) {
            played = false;
          }
        }
        return !timeout && played;
      }
      return false;
    }
  }

  xWithCentre(x: number) {
    return x - this.width / 2;
  }

  yWithCentre(y: number) {
    return y - this.height / 2;
  }

  move(targetPos: number, speed: number) {
    this.scene.tweens.add({
      targets: this,
      x: this.xWithCentre(targetPos),
      duration: 1000 / speed,
      ease: "Power2",
      onUpdate: this.draw,
      callbackScope: this,
    });
  }

  show() {
    this.isPaused = false;
    this.scene.tweens.add({
      targets: this,
      alpha: colors.cursorAlpha,
      duration: 1000,
      ease: "Power2",
      onUpdate: this.draw,
      callbackScope: this,
    });
  }

  pause() {
    this.isPaused = true;
  }

  unpause() {
    this.isPaused = false;
  }

  destroy() {
    this.timedOutKeys.clear();
    // Properly clean up all timers
    this.timers.forEach((timer) => {
      if (timer && timer.destroy) {
        timer.destroy();
      }
    });
    this.timers = [];
    this.heldKeys.clear();
    this.wrongNotes = [];
    this.endX = 0;
    this.currentNote = -1;
    this.onFinish = () => {};
    this.cbCtx = undefined;
    super.destroy();
  }
}

export default class NoTimeExercise extends ExerciseBase<Objective, Config> {
  config?: Config;
  private notes: VoiceData[][] = [];
  listening: boolean = true;
  cursor?: NoTimeCursor;
  appearanceDelay?: Phaser.Time.TimerEvent;

  phrase?: PhraseGraphic;

  constructor() {
    super("NoTimeExercise");
  }

  stop() {
    this.scene.stop();
  }

  getNotes(id: number) {
    return this.notes[id];
  }

  getAllSlices() {
    return this.notes;
  }

  getNote(id: string) {
    return this.phrase?.notes.get(id);
  }

  getNotePosition(id: string) {
    return this.phrase?.getNotePosition(id);
  }

  updateObjective({ chapter }: { chapter: number }) {
    console.debug("updating objective");
    if (!this.config || chapter !== this.config.chapterIdx) {
      console.debug(
        "chapter doesnt match objective!",
        chapter,
        this.config?.chapterIdx,
      );
      return;
    } else {
      console.debug("chapter matches!");
    }
    const nextObjective =
      this.currentObjective !== undefined ? this.currentObjective + 1 : 0;

    // Guard: check bounds BEFORE incrementing to avoid leaving state invalid
    if (nextObjective >= this.config.objectives.length) {
      console.warn(
        "NoTime.updateObjective: next objective would be out of bounds, ignoring",
        {
          currentObjective: this.currentObjective,
          nextObjective,
          objectivesLength: this.config.objectives.length,
        },
      );
      return;
    }

    const objective = this.config.objectives[nextObjective];
    if (!objective) {
      console.warn(
        "NoTime.updateObjective: objective is undefined at index",
        nextObjective,
      );
      return;
    }

    // Only update currentObjective after validation passes
    this.currentObjective = nextObjective;

    super.showInstruction(
      objective.text ?? "Play these notes at your own pace.",
    );
    this.transitionIn();
    //if (this.currentObjective > 0) {
    console.debug("obj: ", this.currentObjective);
    const { phrases, formatConfig } = objective;
    this.createGrandStaff(
      phrases![0],
      formatConfig,
      (this.currentObjective ?? 0) === 0 ? "Draw" : "None",
    );
    //None
  }

  unload() {
    // Stop listening immediately to prevent any stale events
    this.listening = false;
    RemoveListener(ReactToExerciseEventType.UpdateObjective);
    this.unloadGraphics();
    super.unload();
  }

  unloadGraphics() {
    // Clean up graphics in proper order
    if (this.cursor) {
      this.cursor.destroy();
      this.cursor = undefined;
    }
    if (this.phrase) {
      this.phrase.destroy();
      this.phrase = undefined;
    }
    if (this.appearanceDelay) {
      this.appearanceDelay.destroy();
      this.appearanceDelay = undefined;
    }
    // Clear notes array
    this.notes = [];
  }

  startExercise() {
    this.cursor?.show();
    this.listening = true;
  }

  pauseExercise(): void {
    // Stop listening to prevent stale events on resume
    this.listening = false;
    this.cursor?.pause();
    super.pauseExercise();
  }

  resume(): void {
    // Only resume listening if we have an active objective
    if (this.currentObjective !== undefined && this.currentObjective >= 0) {
      this.listening = true;
      this.cursor?.unpause();
    }
    super.resume();
  }

  createGrandStaff(
    phrase: PhraseObjectiveData,
    config?: FormatConfig,
    transition?: StaffTransition,
  ) {
    console.debug(this.config);
    console.debug(phrase);
    const data = new PhraseData(
      0,
      phrase.cursorData,
      phrase.uuid,
      new TimeSignature(4, 4),
      false,
      true,
      true,
      true,
      0,
    );
    const phraseWidth = this.config!.width ?? 500;
    const x =
      this.config!.centre_align_horizontal
        ? (this.cameras.main.width / 2) / window.devicePixelRatio - phraseWidth / 2
        : (this.config!.padding_x ?? 0) / window.devicePixelRatio;
    console.debug("padding_x", x, this.config!.centre_align_horizontal, phraseWidth);
    this.phrase = new PhraseGraphic(
      this,
      x,
      {
        globalScaling: 1,
        pitchLineGap: 20,
        pitchLineWidth: 2,
        measureLineWidth: 3,
        clefGap: 100,
        centreAlign: false,
        doubleEndLines: true,
      },
      data,
      //(this.currentObjective ?? 0) === 0 ? 'Draw' : 'Fade',
      "None",
      0,
      0,
    );
    const width = this.cameras.main.width - x;
    const height = 320;
    const actualVpHeight = this.cameras.main.height;
    const props = {
      minPaddingX: 50,
      minPaddingY: 50,
      idealPhraseConfig: MediumConfig(),
      minPhraseConfig: MinimumConfig(),
      minStaffSpacingVertical: 35,
      idealPaddingX: 75,
      idealPaddingY: 75,
    };
    const extraStaffParams = {
      topClefNumExtraLinesTop: 4,
      topClefNumExtraLinesBottom: 2,
      bottomClefNumExtraLinesTop: 2,
      bottomClefNumExtraLinesBottom: 4,
    };
    const minAspectRatio = 4.5;
    const maxAspectRatio = 8;
    const resizeParams = optimizeStaffForOptions(
      [width, height],
      props,
      extraStaffParams,
      minAspectRatio,
      maxAspectRatio,
      1,
    );
    if (resizeParams) {
      const maxWidth = resizeParams.phraseDimens.innerHeight * maxAspectRatio;
      const availableWidth = width - resizeParams.paddingX * 2;
      console.debug(
        "availableWidth",
        availableWidth,
        "maxWidth",
        maxWidth,
        "adjustedVpWidth",
        width,
        "vpWidth",
        this.cameras.main.width,
        "paddingX",
        resizeParams.paddingX,
        "x",
        x,
      );
      //const phraseWidth = Math.min(availableWidth, maxWidth);
      const phraseHeight = resizeParams.phraseDimens.bbHeight;
      const phraseY =
        (actualVpHeight - phraseHeight) / 2 + resizeParams.paddingY;
      console.debug("phraseX", x, resizeParams.paddingX);
      this.phrase.resize(
        { ...resizeParams.phraseRenderingConfig, doubleEndLines: true },
        //phraseWidth / window.devicePixelRatio,
        phraseWidth,
        //resizeParams.paddingX / window.devicePixelRatio,
        x,
        phraseY,
        resizeParams.phraseDimens,
        resizeParams.phraseDimens.innerHeight,
      );
      this.phrase.transition(transition ?? "None");
    }
    this.notes = phrase.cursorData.serializedIterator.map(
      ({ currentVoiceEntries, currentTimestamp }) =>
        currentVoiceEntries.flatMap((voiceEntry) =>
          voiceEntry.Notes.filter((n) => !n.isRest && n.printObject).map(
            (note) =>
              ({
                length: 0.25,
                timestamp: currentTimestamp,
                measureIdx: 0,
                voice: { note: noteFromOsmdPitch(note.pitch) },
                id: note.graphics[0].staveNote.id,
                x: 0, // NOTE: Do not use this x in the cursor. Use PhraseGraphic.getNotePosition(id) instead
              } as VoiceData),
          ),
        ),
    );
    const y = this.centreHeight(this.phrase.getInnerHeight());
    this.phrase.setY(y);
    const cursorHeight = this.phrase.getInnerHeight() * 1.25;
    const cursorWidth = cursorHeight * 0.1;
    const cursorY = this.centreHeight(cursorHeight);
    const cursorX = this.phrase.getFirstNotePosition() - cursorWidth / 2;
    this.cursor = new NoTimeCursor(
      this,
      cursorX,
      cursorY,
      cursorWidth,
      cursorHeight,
      this.phrase.getNextPosition(),
      this,
      this.onDone,
      this,
      this.config?.cbType,
    );
  }

  midiOn({ note }: { note: number; velocity: number }) {
    console.debug("exercise: key: ", note);
    if (this.listening && !this.paused) this.cursor?.onPressKey(note);
  }

  midiOff({ note }: { note: number; velocity: number }) {
    this.cursor?.onReleaseKey(note);
  }

  onDone() {
    // Guard: Don't complete if we're paused or not listening
    if (this.paused || !this.listening) {
      return;
    }
    super.passObjective(this.isLastObjective());
  }

  create() {
    EventBus.emit("current-scene-ready", this);
  }

  onObjectivePassed() {
    this.cursor?.destroy();
    this.phrase?.destroy();
    super.onObjectivePassed();
  }

  transitionIn() {
    (this.cameras.main.getPostPipeline("Dissolve") as Dissolve).progress = 1;
    console.debug("[show] sending show");
    NotifyReact(ShowExercise());
  }

  transition(skip?: boolean, last?: boolean) {
    this.tweens.add({
      targets: this.cameras.main.getPostPipeline("Dissolve") as Dissolve,
      progress: 0,
      duration: 1500,
      onComplete: () => super.transition(skip, last),
      callbackScope: this,
    });
  }

  init(config: Config) {
    console.debug("objective: init", config.chapterIdx);
    console.debug("[Colorblind] cbType", config.cbType);
    super.init(config);
    this.config = DefaultConfig(config);
    if (this.config.centre_align_horizontal) {
      this.config.padding_x =
        this.cameras.main.width / 2 - this.config.width! / 2;
    }
    /*console.debug(this.config);
    let delay = this.config.firstObjectiveAppearDelay ?? 0;
    this.appearanceDelay = this.time.delayedCall(
      delay,
      () => this.createGrandStaff(this.config!.objectives[0].phrases![0]),
      [],
      this
    );*/
    this.cameras.main.setPostPipeline("Dissolve");
    (this.cameras.main.getPostPipeline("Dissolve") as Dissolve).progress = 1;
    ListenForReactEvent(
      ListenUpdateObjective(this.updateObjective),
      this,
      false,
      true,
    );
  }
}
